/**
 * useCrisisDetection
 * Detects suicidal ideation and acute psychological crisis in user text input.
 * Based on WHO Safe Messaging Guidelines and AFSP recommendations.
 */

const CRISIS_PATTERNS = [
  // Direct suicidal ideation
  /\b(want|wanna|wish|plan|going)\s+to\s+(die|kill\s+myself|end\s+it|end\s+my\s+life)\b/i,
  /\b(suicid(e|al|io)|self.?harm|self.?hurt|cut\s+myself)\b/i,
  /\b(better\s+off\s+dead|better\s+off\s+without\s+me)\b/i,
  /\b(no\s+reason\s+to\s+live|nothing\s+to\s+live\s+for|life\s+is\s+not\s+worth)\b/i,
  /\b(end\s+it\s+all|end\s+everything|finish\s+everything)\b/i,
  /\b(don'?t\s+want\s+to\s+(be\s+here|exist|go\s+on))\b/i,
  /\b(can'?t\s+(go\s+on|take\s+it\s+anymore|keep\s+going|continue))\b/i,
  // Portuguese
  /\b(quero\s+morrer|quero\s+me\s+matar|não\s+quero\s+(mais\s+)?viver)\b/i,
  /\b(suicíd[ioa]|suicidio|autolesã[oa]|cortar.?me)\b/i,
  /\b(melhor\s+(estar\s+)?morto|melhor\s+sem\s+mim)\b/i,
  /\b(sem\s+razão\s+para\s+viver|não\s+vale\s+a\s+pena\s+viver)\b/i,
  /\b(acabar\s+com\s+tudo|acabar\s+com\s+a\s+minha\s+vida)\b/i,
  /\b(só\s+quero\s+morrer|apenas\s+quero\s+morrer)\b/i,
  /\b(não\s+consigo\s+(continuar|seguir\s+em\s+frente))\b/i,
  // Spanish
  /\b(quiero\s+morir|quiero\s+matarme|no\s+quiero\s+vivir)\b/i,
  /\b(suicid[ioa]|autolesión|cortarme)\b/i,
  // French
  /\b(veux\s+mourir|veux\s+me\s+tuer|plus\s+envie\s+de\s+vivre)\b/i,
  /\b(suicide|automutilation)\b/i,
  // German
  /\b(sterben\s+wollen|mich\s+umbringen|nicht\s+mehr\s+leben\s+wollen)\b/i,
  /\b(selbstmord|suizid|selbstverletzung)\b/i,
];

/**
 * Returns true if the text contains crisis signals.
 */
export function detectCrisis(text) {
  if (!text || typeof text !== 'string' || text.trim().length < 3) return false;
  return CRISIS_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * React hook that monitors a text value and returns isCrisis flag.
 * Usage: const isCrisis = useCrisisDetection(formData.emotional_context);
 */
import { useMemo } from 'react';

export default function useCrisisDetection(text) {
  const isCrisis = useMemo(() => detectCrisis(text), [text]);
  return isCrisis;
}
